<template>
  <panel color="blue-4" padding-mode="bottom-only">
    <div class="md-layout case-initial-conditions">
      <div class="md-layout-item md-large-size-100">
        <section-title color="blue-4">
          Patient Conditions
        </section-title>
        <div class="patient-conditions">
          <div class="patient-conditions-title">
            Starting Position & Behavior
            <help-tooltip type="info">Select the patients starting position. This will determine what behaviors the patient can portray.</help-tooltip>
            <help-tooltip v-if="stanceError" type="error">{{ stanceError.message }}</help-tooltip>
          </div>
          <div class="md-layout">
            <patient-stance :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update" />
          </div>

          <div class="patient-conditions-title">Circulation</div>
          <div class="heart-and-block-grid">
            <patient-states :state-ids="['heartRhythm', 'complexion']" :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update" />
            <patient-states :state-ids="['heartRate']" :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update" />
            <patient-states :state-ids="['BPSystolic']" :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" layout-classes="systolic-state-container" @update="update">
              <div class="pulse-bp-sync-container">
                <md-checkbox class="pulse-bp-sync-checkbox" :value="caseConf.initialConditions ? !caseConf.initialConditions.disablePulseBpSync : true" @change="value => togglePulseBpSync(value)">Sync to Pulse</md-checkbox>
                <help-tooltip>Matches initial Pulse Strength to Systolic Pressure</help-tooltip>
              </div>
            </patient-states>
            <patient-states :state-ids="['BPDiastolic']" :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update" />
          </div>
        </div>

        <div class="patient-conditions-title">Breathing</div>
        <div class="wellness-grid">
          <patient-temperature-input
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
          <patient-states
            :state-ids="['respiratoryRate', 'spo2', 'etco2', 'glucose']"
            :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update"
          />
          <patient-states :state-ids="['etco2Rhythm']" :no-layout="true" :is-new-case="!caseConf.id" :case-conf="caseConf" :readonly="readonly" @update="update" />
        </div>

        <div class="patient-conditions-title">Measurement Units</div>
        <div class="measurements-container">
          <measurement-units-selector
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
          <temperature-units-selector
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
        </div>

        <section-title color="blue-4">
          Assessments
        </section-title>
        <div class="assessments-container">
          <div class="assessments-item-container">
            <h2>Chest Sounds</h2>
            <md-button class="md-primary md-raised gray-4 bg" @click="openModal('chestSounds')">CHANGE CHEST SOUNDS</md-button>
          </div>

          <div class="assessments-item-container">
            <h2>Pen Light</h2>
            <md-button class="md-primary md-raised gray-4 bg" @click="openModal('penLight')">CHANGE EYES</md-button>
          </div>

          <div class="assessments-item-container">
            <h2>Pulse</h2>
            <md-button class="md-primary md-raised gray-4 bg" @click="openModal('pulse')">CHANGE PULSE STRENGTH</md-button>
          </div>
        </div>
      </div>

      <div class="md-layout-item md-large-size-100">
        <section-title color="blue-4">
          Physical Assessment & Moulage
        </section-title>
        <case-physical-assessments
          :is-new-case="isNewCase"
          :case-conf="caseConf"
          :readonly="readonly"
          @update="update"
        />
        <expose-patient
          :case-conf="caseConf"
          :readonly="readonly"
          @update="update"
        />
        <div class="weight-height-container">
          <patient-height-input
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
          <patient-weight-input
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
        </div>
      </div>
    </div>

    <md-dialog
      class="gray-3 bg chest-sounds-dialog"
      :md-active="state.activeModal === 'chestSounds'"
      :md-close-on-esc="true"
      :md-fullscreen="false"
      @md-clicked-outside="closeModal"
    >
      <md-dialog-content>
        <section-title class="section-title" color="blue-4">
          Chest Sounds
        </section-title>

        <div class="sides-container">
          <div v-for="(chestSoundSpotGroup, index) in chestSoundSpotGroupsForAvatar" :key="index" class="sounds-container" :span="12">
            <h2 class="sounds-title blue-4 border">{{ chestSoundSpotGroup.name }}</h2>
            <case-sounds-container
              :case-conf="caseConf"
              :spots="chestSoundSpotGroup.spots"
              :category="chestSoundSpotGroup.category"
              :orientation="chestSoundSpotGroup.orientation"
              :background="chestSoundSpotGroup.picture"
              :overlay="chestSoundSpotGroup.overlay"
              :overlays="chestSoundSpotGroup.overlays"
              :readonly="readonly"
              @update="update"
            />
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="closeModal">Done</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog
      class="gray-3 bg"
      :md-active="state.activeModal === 'penLight'"
      :md-close-on-esc="true"
      :md-fullscreen="false"
      @md-clicked-outside="closeModal"
    >
      <md-dialog-content>
        <case-eyes :is-new-case="isNewCase" :case-conf="caseConf" :readonly="readonly" @update="update" />
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="closeModal">Done</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog
      class="gray-3 bg pulse-dialog"
      :md-active="state.activeModal === 'pulse'"
      :md-close-on-esc="true"
      :md-fullscreen="false"
      @md-clicked-outside="closeModal"
    >
      <md-dialog-content>
        <section-title class="section-title" color="blue-4">
          Pulse Strength
        </section-title>

        <div class="pulse-container md-scrollbar md-theme-default">
          <case-pulse
            :case-conf="caseConf"
            :readonly="readonly"
            @update="update"
          />
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="closeModal">Done</md-button>
      </md-dialog-actions>
    </md-dialog>
  </panel>
</template>

<script>
import get from 'lodash/get';

import HelpTooltip from '~lib/common/components/HelpTooltip';

import SectionTitle from '../../common/SectionTitle.vue';
import PatientStates from '../PatientStates';
import PatientStance from '../PatientStance';
import Panel from '../../common/Panel/index.js';
import CaseSoundsContainer from '../CaseSounds';
import CaseEyes from '../CaseEyes';
import CasePulse from '../CasePulse';
import CasePhysicalAssessments from '../CasePhysicalAssessments';
import chestSoundSpotGroups from '../../../config/chestSoundSpots';
import heartSounds from '../../../config/heartSoundSpots';
import ExposePatient from '../ExposePatient';
import MeasurementUnitsSelector from '../MeasurementUnitsSelector';
import TemperatureUnitsSelector from '../TemperatureUnitsSelector';
import PatientHeightInput from '../PatientHeightInput';
import PatientWeightInput from '../PatientWeightInput';
import PatientTemperatureInput from '../PatientTemperatureInput';

export default {
  name: 'case-initial-conditions',
  components: {
    HelpTooltip,
    SectionTitle,
    PatientStates,
    PatientStance,
    Panel,
    CaseSoundsContainer,
    CaseEyes,
    CasePhysicalAssessments,
    ExposePatient,
    MeasurementUnitsSelector,
    TemperatureUnitsSelector,
    PatientHeightInput,
    PatientWeightInput,
    PatientTemperatureInput,
    CasePulse,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        activeModal: null,
      },
      grading: 'neutral',
      isNewCase: false,
    };
  },
  computed: {
    heartSounds() {
      return heartSounds[get(this.caseConf,'_avatar.key','HAWTHORN')];
    },
    chestSoundSpotGroupsForAvatar() {
      return chestSoundSpotGroups[get(this.caseConf,'_avatar.key','HAWTHORN')];
    },
    stanceError() {
      let errors = []
      if (this.$store) {
        errors = get(this.$store.getters['validation/errorsByFieldAndId'], '_intialStates.position', []);
      }
      return errors.length > 0 ? errors[0] : null;
    },
  },
  mounted() {
    this.isNewCase = !this.caseConf.id;
  },
  methods: {
    update(...args) {
      this.$emit('update', ...args);
    },
    openModal(modal) {
      this.state.activeModal = modal;
    },
    closeModal() {
      this.state.activeModal = null;
    },
    togglePulseBpSync(sync) {
      this.$emit('update', {
        initialConditions: {
          disablePulseBpSync: !sync
        }
      });
    },
  },
};
</script>

<style scoped>
.case-initial-conditions >>> .md-layout-item {
  padding-right: 20px;
}
.patient-conditions {
  display: flex;
  flex-direction: column;
}
.patient-conditions-title {
  font-size: 24px;
  font-weight: 500;
  padding-bottom: 12px;
}
.patient-conditions-title:not(:first-child) {
  margin-top: 20px;
}
.heart-and-block-grid {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 2fr 1fr;
  grid-gap: 12px;
  max-width: 400px;
}
.heart-and-block-grid :first-child {
  grid-row: 1;
}
.heart-and-block-grid :nth-child(2) {
  grid-row: 2;
}
.heart-and-block-grid :nth-child(3) {
  grid-column: 2;
}
.heart-and-block-grid :nth-child(4) {
  grid-column: 2;
}
.heart-and-block-grid :nth-child(5) {
  grid-column: 2;
}
.wellness-grid {
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 12px;
  max-width: 400px;
}
.wellness-grid :nth-child(6) {
  grid-column: span 2;
}
.section-title {
  margin-bottom: 20px;
}
.measurements-container {
  display: flex;
  margin-bottom: 20px;
}
.temperature-units-selector {
  margin-left: 20px;
}
.assessments-container {
  margin-bottom: 35px;
}
.assessments-item-container {
  display: flex;
  align-items: center;
}
.assessments-item-container > * {
  width: 200px;
}

.md-dialog {
  min-width: 600px;
}
.heart-sounds-dialog {
  width: 35%;
}
.chest-sounds-dialog {
  width: 55%;
  height: 90%;
  max-height: 90%;
}
.sounds-title {
  line-height: normal;
  margin: 0;
  margin-bottom: 10px;
  width: 100%;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: solid 3px;
}
.sides-container {
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  overflow: hidden;
}
.sounds-container {
  padding: 10px 0;
}
.pulse-container {
  height: calc(100% - 72px);
  overflow: auto;
}
.case-initial-conditions .pulse-bp-sync-container {
  position: absolute;
  right: -190px;
  top: 10px;
  display: flex;
  align-items: center;
}
.weight-height-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 10px;
}

.weight-height-container > div {
  margin-right: 30px;
}

.pulse-bp-sync-checkbox >>> .md-ripple {
  position:inherit;
}
</style>
